import { queryOptions } from '@tanstack/react-query'

import { keys } from '@/api/keys'

/**
 * The one HTTP transport for `/api`.
 *
 * - Every request carries `X-Studio-User` (single-operator identity, §0).
 * - Error envelopes `{error:{code,message,details}}` (§4.8) become an
 *   `ApiError` so callers can switch on `err.code`.
 * - Asset uids contain `:` and `/` and go into paths **verbatim** — never
 *   `encodeURIComponent` a uid (§5 preamble). Use `path\`…\`` for that.
 * - Query-string values are encoded normally.
 */

export const API_BASE: string = (import.meta.env.VITE_API_BASE ?? '').replace(/\/$/, '')
export const STUDIO_USER_HEADER = 'X-Studio-User'
/** Fired on `window` when the server refuses a request for want of a session (`features/auth/AuthGate`). */
export const UNAUTHORIZED_EVENT = 'studio:unauthorized'

let studioUser = 'operator'

/** Change the identity recorded as provenance (there is no login in v1). */
export function setStudioUser(name: string): void {
  studioUser = name.trim() || 'operator'
}

export function getStudioUser(): string {
  return studioUser
}

export interface ApiErrorEnvelope {
  error: { code: string; message: string; details?: Record<string, unknown> }
}

/** Thrown for any non-2xx response and for network failures (`status: 0`, `code: 'network'`). */
export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly details: Record<string, unknown>
  readonly path: string

  constructor(status: number, code: string, message: string, path: string, details: Record<string, unknown> = {}) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.details = details
    this.path = path
  }

  /** `409 conflict` on a stale `base_rev`; `details.current_rev` carries the head. */
  get isConflict(): boolean {
    return this.status === 409
  }

  get isNotFound(): boolean {
    return this.status === 404
  }

  /** AI cooldown, usage window, or the AI adapter being down. */
  get isAiUnavailable(): boolean {
    return this.code === 'ai_unavailable' || this.code === 'ai_rate_limited'
  }

  /** Request id from the 500 envelope, when present. */
  get requestId(): string | undefined {
    const id = this.details['request_id']
    return typeof id === 'string' ? id : undefined
  }
}

export function isApiError(err: unknown): err is ApiError {
  return err instanceof ApiError
}

function isEnvelope(x: unknown): x is ApiErrorEnvelope {
  if (typeof x !== 'object' || x === null) return false
  const e = (x as { error?: unknown }).error
  return typeof e === 'object' && e !== null && typeof (e as { code?: unknown }).code === 'string'
}

export type QueryValue = string | number | boolean | null | undefined
export type QueryParams = Record<string, QueryValue | QueryValue[]>

/** Build `?a=1&b=x` from a params object; `undefined`, `null` and `''` are skipped. */
export function qs(params?: QueryParams): string {
  if (!params) return ''
  const sp = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    const values = Array.isArray(v) ? v : [v]
    for (const val of values) {
      if (val === undefined || val === null || val === '') continue
      sp.append(k, String(val))
    }
  }
  const s = sp.toString()
  return s ? `?${s}` : ''
}

/**
 * Tagged template for API paths with verbatim uids:
 * ``path`/api/annotations/${uid}/revisions` `` keeps `char:people/royalty/king`
 * exactly as-is. Non-uid interpolations (numbers, ids) are stringified the same way.
 */
export function path(strings: TemplateStringsArray, ...values: (string | number)[]): string {
  let out = ''
  strings.forEach((s, i) => {
    out += s
    if (i < values.length) out += String(values[i])
  })
  return out
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  /** JSON-serialised unless it is already a `BodyInit`. */
  body?: unknown
  query?: QueryParams
  signal?: AbortSignal
  headers?: Record<string, string>
}

function isBodyInit(body: unknown): body is BodyInit {
  return body instanceof FormData || body instanceof Blob || body instanceof ArrayBuffer || body instanceof URLSearchParams || typeof body === 'string'
}

/**
 * Fetch JSON from `/api/...`. Resolves with the parsed body (`undefined` for
 * 204/empty), rejects with `ApiError`. `path` must start with `/api`.
 */
export async function fetchJson<T>(apiPath: string, opts: RequestOptions = {}): Promise<T> {
  const url = `${API_BASE}${apiPath}${qs(opts.query)}`
  const headers: Record<string, string> = {
    Accept: 'application/json',
    [STUDIO_USER_HEADER]: studioUser,
    ...opts.headers,
  }
  let body: BodyInit | undefined
  if (opts.body !== undefined) {
    if (isBodyInit(opts.body)) {
      body = opts.body
    } else {
      headers['Content-Type'] = 'application/json'
      body = JSON.stringify(opts.body)
    }
  }

  let res: Response
  try {
    res = await fetch(url, { method: opts.method ?? 'GET', headers, body, signal: opts.signal, credentials: 'same-origin' })
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err
    throw new ApiError(0, 'network', 'Could not reach the studio server.', apiPath)
  }

  if (res.status === 204 || res.headers.get('content-length') === '0') {
    if (!res.ok) throw new ApiError(res.status, 'internal', res.statusText || `HTTP ${res.status}`, apiPath)
    return undefined as T
  }

  const text = await res.text()
  let parsed: unknown = undefined
  if (text) {
    try {
      parsed = JSON.parse(text)
    } catch {
      if (res.ok) throw new ApiError(res.status, 'bad_response', 'The server sent something that is not JSON.', apiPath)
    }
  }

  if (!res.ok) {
    if (res.status === 401 && typeof window !== 'undefined') window.dispatchEvent(new Event(UNAUTHORIZED_EVENT))
    if (isEnvelope(parsed)) {
      throw new ApiError(res.status, parsed.error.code, parsed.error.message, apiPath, parsed.error.details ?? {})
    }
    throw new ApiError(res.status, codeForStatus(res.status), text.slice(0, 200) || res.statusText || `HTTP ${res.status}`, apiPath)
  }
  return parsed as T
}

function codeForStatus(status: number): string {
  switch (status) {
    case 400:
      return 'bad_request'
    case 404:
      return 'not_found'
    case 409:
      return 'conflict'
    case 422:
      return 'validation_error'
    case 423:
      return 'not_published'
    case 429:
      return 'ai_rate_limited'
    case 503:
      return 'unavailable'
    default:
      return status >= 500 ? 'internal' : 'error'
  }
}

/* ------------------------------------------------------------------------ */
/* Health — the one cross-cutting query every screen may read (§5.0).        */
/* ------------------------------------------------------------------------ */

export type AiStatus = 'ok' | 'degraded' | 'down' | 'mock'

export interface Health {
  status: 'ok' | 'degraded'
  version: string
  db: { ok: boolean; schema_rev?: string }
  s3: { ok: boolean; last_listing_at?: string | null; endpoint?: string }
  cache: { bytes: number; max_bytes: number; free_bytes: number }
  tts: { ok: boolean; url?: string }
  ai: {
    status: AiStatus
    mode?: string
    auth_method?: string | null
    subscription?: boolean | null
    cli_version?: string | null
    billing?: 'api' | 'subscription' | 'mock'
    last_error?: string | null
    checked_at?: string
    clock_skew_s?: number
  }
  jobs: {
    by_lane: Record<string, { queued: number; running: number }>
    paused_until?: string
  }
}

export function fetchHealth(signal?: AbortSignal): Promise<Health> {
  return fetchJson<Health>('/api/health', { signal })
}

/** Polls every 30 s; SSE `ai.health` events refresh it sooner. Never throws to the UI (see `retry`). */
export const healthQuery = queryOptions({
  queryKey: keys.health(),
  queryFn: ({ signal }) => fetchHealth(signal),
  staleTime: 15_000,
  refetchInterval: 30_000,
  retry: 1,
})
